/**
 * Visual Discovery Page
 * Image-based music discovery
 */

import { toast } from 'react-hot-toast';
import Masonry from 'react-masonry-css';
import { useVisualStore } from '../stores/visual-store';
import { usePlaylistExportStore } from '../stores/playlist-export.store';
import { generateVisualDiscovery } from '../services/visual.service';
import ImageUploadZone from '../components/visual/ImageUploadZone';
import VisualAnalysisCard from '../components/visual/VisualAnalysisCard';
import TrackCard from '../components/TrackCard';
import EmptyState from '../components/EmptyState';
import ExportPlaylistModal from '../components/ExportPlaylistModal';
import { wanderingModal, WanderingModal } from '../components/WanderingModal';

export default function VisualDiscovery() {
  const {
    images,
    results,
    imageAnalyses,
    interpretation,
    loading,
    error,
    cacheHit,
    responseTimeMs,
    isValid,
    setLoading,
    setError,
    setResults,
  } = useVisualStore();

  const { openExportModal } = usePlaylistExportStore();

  const handleGenerate = async () => {
    if (!isValid()) {
      toast.error('Upload 1-4 images to generate recommendations');
      return;
    }

    wanderingModal.show();
    setLoading(true);
    setError(null);

    // Show loading toast
    toast.loading('Analyzing images...', { id: 'visual-toast', duration: Infinity });

    try {
      const files = images.map(img => img.file);
      const response = await generateVisualDiscovery(files);

      setResults(response);
      toast.dismiss('visual-toast');

      if (response.cache_hit) {
        toast.success('Results from cache');
      } else {
        toast.success(`Generated ${response.recommendations.length} recommendations!`);
      }
    } catch (err: any) {
      toast.dismiss('visual-toast');
      const errorMessage = err?.message || 'An error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      wanderingModal.hide();
    }
  };

  const breakpointColumns = {
    default: 2,
    1024: 1,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Visual Discovery
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Drop an image. Find what it sounds like.
          </p>
        </div>

        {/* Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: Upload Zone */}
          <div className="lg:col-span-5 space-y-6">
            <ImageUploadZone />

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={!isValid() || loading}
              className="w-full px-6 py-4 bg-blue-600 text-white text-lg font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md"
            >
              {loading ? 'Reading the room...' : 'Find the sound'}
            </button>

            {/* Help Text */}
            {!loading && images.length === 0 && (
              <div className="bg-gray-100 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">How it works</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Drop 1–4 images that move you</li>
                  <li>• We read the mood, texture, and atmosphere</li>
                  <li>• You get music that fits the feeling</li>
                  <li>• Album art, stills, photography — all fair game</li>
                </ul>
              </div>
            )}
          </div>

          {/* Right: Results */}
          <div className="lg:col-span-7">
            {loading ? (
              // Loading State
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Reading the room...
                </h3>
                <p className="text-gray-600 text-center max-w-md">
                  Pulling the mood out of these images. Give us a beat.
                </p>
              </div>
            ) : error ? (
              // Error State
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 text-red-600 mt-0.5 mr-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <h3 className="text-sm font-medium text-red-800 mb-1">Error</h3>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            ) : results.length > 0 ? (
              // Results
              <div className="space-y-6">
                {/* Analysis Card */}
                {interpretation && (
                  <VisualAnalysisCard analyses={imageAnalyses} interpretation={interpretation} />
                )}

                {/* Results Header */}
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {results.length} Recommendations
                  </h2>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 text-xs">
                      {cacheHit && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                          Cached
                        </span>
                      )}
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                        {(responseTimeMs / 1000).toFixed(1)}s
                      </span>
                    </div>
                    <button
                      onClick={() => openExportModal(results)}
                      className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Export to Playlist
                    </button>
                  </div>
                </div>

                {/* Masonry Grid */}
                <Masonry
                  breakpointCols={breakpointColumns}
                  className="flex -ml-4 w-auto"
                  columnClassName="pl-4 bg-clip-padding"
                >
                  {results.map(track => (
                    <div key={track.id} className="mb-4">
                      <TrackCard track={track} />
                    </div>
                  ))}
                </Masonry>
              </div>
            ) : (
              // Empty State
              <EmptyState
                icon="🎨"
                title="Nothing here yet."
                description="Drop an image and hit Find the sound."
              />
            )}
          </div>
        </div>
      </div>

      <ExportPlaylistModal />
      <WanderingModal allowDismiss onDismiss={() => { setLoading(false); wanderingModal.hide(); }} />
    </div>
  );
}
